import { useState } from "react";
import { C, uid, fmt, Btn, Input, Select, StatCard, useIsMobile } from "./ui";
import { toast } from "../services/toast";

export function MaquinariaPanel({ maquinarias, onUpdate, user, profile }) {
  const isMobile = useIsMobile();
  const [show, setShow] = useState(false);
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("Tractor");
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [anio, setAnio] = useState("");
  const [obs, setObs] = useState("");
  const [confirmar, setConfirmar] = useState(null);

  const TIPOS = ["Tractor", "Pulverizador", "Sembradora", "Cosechadora", "Arado", "Rastra", "Otra"];

  const crear = async () => {
    if (!nombre) return;
    try {
      const nueva = {
        id: uid(),
        nombre: nombre.trim(),
        tipo,
        marca: marca.trim(),
        modelo: modelo.trim(),
        anio: anio ? parseInt(anio) : new Date().getFullYear(),
        horasUso: 0,
        costoMantenimiento: [],
        obs: obs.trim(),
        createdAt: new Date().toISOString(),
      };
      await onUpdate([...maquinarias, nueva]);
      setShow(false);
      setNombre("");
      setTipo("Tractor");
      setMarca("");
      setModelo("");
      setAnio("");
      setObs("");
      toast.success("Máquina creada.");
    } catch (e) {
      console.error("Error creando máquina:", e);
      toast.error("No se pudo crear la máquina: " + (e.message || "error"));
    }
  };

  const eliminar = async (id) => {
    try {
      await onUpdate(maquinarias.filter(m => m.id !== id));
      setConfirmar(null);
      toast.success("Máquina eliminada.");
    } catch (e) {
      console.error("Error eliminando máquina:", e);
      toast.error("No se pudo eliminar.");
    }
  };

  const registrarMantenimiento = async (id, monto) => {
    const m = maquinarias.find(x => x.id === id);
    if (!m || !monto) return;
    try {
      const hoy = new Date().toISOString().slice(0, 10);
      const costos = [...(m.costoMantenimiento || []), { id: uid(), fecha: hoy, monto: parseFloat(monto) || 0, obs: "" }];
      const actualizada = { ...m, costoMantenimiento: costos };
      await onUpdate(maquinarias.map(x => x.id === id ? actualizada : x));
      toast.success("Mantenimiento registrado.");
    } catch (e) {
      console.error("Error:", e);
      toast.error("No se pudo registrar.");
    }
  };

  const totalHoras = maquinarias.reduce((s, m) => s + (m.horasUso || 0), 0);
  const totalCostos = maquinarias.reduce((s, m) => s + (m.costoMantenimiento || []).reduce((ss, c) => ss + (c.monto || 0), 0), 0);

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", maxWidth: 880, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>
          Maquinaria
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>
          Registro de máquinas y mantenimientos.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(100px,1fr))", gap: 12, marginBottom: 14 }}>
          <StatCard val={maquinarias.length} label="Máquinas" />
          <StatCard val={Math.round(totalHoras)} label="Horas acumuladas" />
          <StatCard val={`$${totalCostos.toLocaleString("es-UY")}`} label="Mantenimiento total" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <Btn variant="primary" onClick={() => setShow(v => !v)}>{show ? "Cancelar" : "+ Nueva máquina"}</Btn>
        </div>

        {show && (
          <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${C.arenaDark}` }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "0 12px" }}>
              <Input label="Nombre" value={nombre} onChange={setNombre} placeholder="Ej: Tractor rojo" />
              <Select label="Tipo" value={tipo} onChange={setTipo} options={TIPOS} />
              <Input label="Marca" value={marca} onChange={setMarca} placeholder="Ej: John Deere" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0 12px", marginTop: 12 }}>
              <Input label="Modelo" value={modelo} onChange={setModelo} placeholder="Ej: 7R" />
              <Input label="Año" value={anio} onChange={setAnio} type="number" placeholder={new Date().getFullYear().toString()} />
            </div>
            <div style={{ marginTop: 12 }}>
              <Input label="Observaciones (opcional)" value={obs} onChange={setObs} placeholder="Ej: Segunda máquina, en uso" />
            </div>
            <Btn variant="primary" onClick={crear} disabled={!nombre} style={{ width: "100%", marginTop: 12 }}>Crear máquina</Btn>
          </div>
        )}

        {maquinarias.length === 0 && !show && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🚜</div>
            <div style={{ fontSize: 13 }}>Registrá tu maquinaria para llevar el control de mantenimientos.</div>
          </div>
        )}

        {maquinarias.map((m) => (
          <div key={m.id} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: `1px solid ${C.arenaDark}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 30 }}>🚜</span>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.tierra }}>{m.nombre}</div>
                <div style={{ fontSize: 12, color: C.muted }}>
                  {m.tipo} · {m.marca} {m.modelo} ({m.anio || "—"}) · {m.horasUso || 0} horas
                </div>
              </div>
              {confirmar === m.id ? (
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => { eliminar(m.id); setConfirmar(null); }} style={{ ...btnZ, background: C.danger, color: "#fff", borderColor: C.danger }}>Sí, borrar</button>
                  <button onClick={() => setConfirmar(null)} style={btnZ}>No</button>
                </div>
              ) : (
                <button onClick={() => setConfirmar(m.id)} style={{ ...btnZ, color: C.danger, borderColor: "#E0909055" }}>✕</button>
              )}
            </div>

            {/* Mantenimientos */}
            <div style={{ marginTop: 12, borderTop: `1px solid ${C.arena}`, paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.tierra, marginBottom: 8 }}>🔧 Mantenimientos</div>
              {(m.costoMantenimiento || []).length === 0 ? (
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Sin registros.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8, maxHeight: 120, overflowY: "auto", paddingRight: 4 }}>
                  {m.costoMantenimiento.map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", fontSize: 11, background: C.fondo, borderRadius: 6, padding: "4px 8px" }}>
                      <span style={{ color: C.muted, fontFamily: "monospace", minWidth: 60 }}>{fmt(c.fecha)}</span>
                      <span style={{ flex: 1 }}>$</span>
                      <span style={{ fontWeight: 700, color: C.tierra }}>${(c.monto || 0).toLocaleString("es-UY")}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "flex-end" }}>
                <Input type="number" label="Costo ($)" placeholder="0" id={`mant_${m.id}`} />
                <Btn size="sm" onClick={() => {
                  const inp = document.getElementById(`mant_${m.id}`);
                  if (inp) registrarMantenimiento(m.id, inp.value);
                }}>Registrar</Btn>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const btnZ = {
  background: C.arena, border: `1px solid ${C.arenaDark}`, borderRadius: 6,
  padding: "6px 9px", fontSize: 12, cursor: "pointer", color: C.tierra, fontFamily: "monospace", flexShrink: 0,
};
